import type { EditableItemContextType } from "@repo/helpers";

import { log } from "@repo/helpers";
import { useAlertContext } from "@repo/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { dynamicEditorPageName } from "../../../string";
import type { ITemplateData } from "../templateDataHook";

import DynamicTab from ".";
import { useOnboardingSessionsApi } from "../../../api/onboardingSessionsApi";
import * as Queries from "../../../api/queries";
import { usePermissionsCheck } from "../../../helpers/permissions";
import { useSettingsQuery } from "../../../helpers/queryHooks";
import useTemplateData from "../templateDataHook";
import { getValidator } from "../validation";

interface DTProps<TEntity, TTemplateData extends ITemplateData> {
	itemContext: EditableItemContextType<TEntity>;
	pageName: string & dynamicEditorPageName;
	itemProp: string & keyof TTemplateData;
	otherTemplateData?: Partial<TTemplateData>;
	loaded: boolean;
	save: (email: boolean) => Promise<TEntity>;
	postSave?: () => Promise<void>;
	updateValues: (
		id: string,
		value: any,
		newState: TTemplateData,
		parentComponent?: string,
	) => Promise<{ changed: boolean; newState: TTemplateData }>;
	customFunctions?: (name: string) => () => Promise<void>;
	readonly?: boolean;
	containerClass?: string;
	submitButtonText?: string;
	saveAndEmail?: boolean;
}

export function EditableItemDynamicTab<T, TTemplateData extends ITemplateData>(props: DTProps<T, TTemplateData>) {
	const queryClient = useQueryClient();
	const { itemContext, otherTemplateData, pageName, itemProp, loaded, submitButtonText, saveAndEmail } = props;
	const { updateValues, customFunctions, postSave } = props;
	const { item, editItem, updateItem, overwriteItem, clearChanges, pendingChanges } = itemContext;
	const { onboardingRequirementsQuery } = useOnboardingSessionsApi();
	const { data: onboardingRequirements, loaded: requirementsLoaded } = onboardingRequirementsQuery;
	const [settings, settingsLoaded] = useSettingsQuery();
	const [initialized, setInitialized] = useState(settingsLoaded);

	useEffect(() => {
		settingsLoaded && setInitialized(true);
	}, [settingsLoaded]);
	const allLoaded = settingsLoaded && loaded && requirementsLoaded;
	const customTemplateData = useMemo(
		() => ({ ...otherTemplateData, onboardingRequirements }) as TTemplateData,
		[otherTemplateData, onboardingRequirements],
	);

	const { templateData, dirty } = useTemplateData<TTemplateData>(customTemplateData);
	const validator = useMemo(() => getValidator(item), [item]);
	const updateData = useCallback((s: TTemplateData) => updateItem(s[itemProp] as any), [updateItem]);
	const editData = useCallback((s: TTemplateData) => editItem(s[itemProp] as any), [editItem]);
	const sections = useMemo(
		() => settings.dynamicSections?.filter((s) => s.pageName?.includes(pageName)),
		[settings.dynamicSections, pageName],
	);

	const { fullAPReadOnly } = usePermissionsCheck();
	const { unsavedChangesAlert } = useAlertContext();

	const onCancel = useCallback(() => {
		clearChanges();
	}, [clearChanges]);

	const save = useCallback(
		async (email: boolean) => {
			try {
				const newItem = await props.save(email);
				if (newItem) {
					overwriteItem(newItem);
					await queryClient.invalidateQueries({
						queryKey: [Queries.ONBOARDING_REQUIREMENTS],
					});
				}
			} catch (ex) {
				log(ex, "error");
			}
		},
		[item, pendingChanges, overwriteItem, props.save],
	);

	return (
		<>
			<DynamicTab<TTemplateData>
				loaded={loaded}
				sections={sections}
				validator={validator}
				updateData={updateData}
				editData={editData}
				updateValues={updateValues}
				initialized={initialized}
				entryDisabled={!allLoaded || props.readonly || fullAPReadOnly}
				templateData={templateData}
				dirty={dirty}
				cancel={onCancel}
				name={pageName}
				save={save}
				postSave={postSave}
				customFunctions={customFunctions}
				containerClass={props.containerClass}
				submitButtonText={submitButtonText}
				saveAndEmail={saveAndEmail}
			/>
		</>
	);
}
